import { useFitnessResource, notifyFitnessChanged } from "@/hooks/useFitnessResource";
import { supabase } from "@/integrations/supabase/client";

/**
 * W6 Fitness Mini-Mirror snapshot — single RPC round-trip.
 *
 * Calls `public.get_fitness_snapshot(p_user_id, p_tz)` which returns:
 *   {
 *     weeklyVolumeKg, weeklyWorkoutsCount, todayWorkoutsCount,
 *     weekMuscleGroups: string[], lastWorkout | null,
 *     prsThisMonth: number,
 *     chips: { today: WorkoutSummary[], week: WorkoutSummary[], prs: PRSummary[] }
 *   }
 *
 * Account-bound; refreshes on return, local midnight and Fitness mutations.
 */

export interface WorkoutSummary {
  id: string;
  name: string | null;
  completed_at: string;
  duration_minutes: number | null;
  exercises_count: number;
  source: string | null;
}

export interface PRSummary {
  exercise_name: string;
  weight_kg: number;
  reps: number;
}

export interface FitnessSnapshotData {
  weeklyVolumeKg: number;
  weeklyWorkoutsCount: number;
  todayWorkoutsCount: number;
  weekMuscleGroups: string[];
  lastWorkout: WorkoutSummary | null;
  prsThisMonth: number;
  chips: {
    today: WorkoutSummary[];
    week: WorkoutSummary[];
    prs: PRSummary[];
  };
}

const EMPTY: FitnessSnapshotData = {
  weeklyVolumeKg: 0,
  weeklyWorkoutsCount: 0,
  todayWorkoutsCount: 0,
  weekMuscleGroups: [],
  lastWorkout: null,
  prsThisMonth: 0,
  chips: { today: [], week: [], prs: [] },
};

async function readSnapshot(userId: string, assertCurrent: () => void): Promise<FitnessSnapshotData> {
  const { data, error } = await (supabase as any).rpc("get_fitness_snapshot", {
    p_user_id: userId, p_tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  assertCurrent(); if (error || !data || !data.chips) throw error ?? new Error("Missing fitness snapshot");
  return data as FitnessSnapshotData;
}

export function useFitnessSnapshot() {
  const resource = useFitnessResource(readSnapshot);
  return { ...resource, data: resource.data ?? EMPTY, isLoading: resource.status === "loading", error: resource.status === "error" ? "snapshot failed" : null };
}

export function invalidateFitnessSnapshotCache(): void { notifyFitnessChanged(); }
